import { LuLoaderCircle } from "react-icons/lu";
import LogoWhite from "../../assets/logo/logo-white.svg?react";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { WelcomeFooter } from "../welcome/components/WelcomeFooter";
import { colors, typography } from "../../design/tokens";

/**
 * Premium error/loading screen layout with gradient background.
 * Used for: access denied, country blocking, offline detection, loading states.
 *
 * Replaces legacy NoAccessContent.tsx (with blue_wave.mp4 video).
 * Uses bg-gradient-hero-dark from design system for consistent premium aesthetic.
 */
export function ErrorScreenLayout({
  isLoading,
  info,
  actionBtn,
}: {
  isLoading: boolean;
  info: string;
  actionBtn?: {
    label: string;
    onClick: () => void;
  };
}) {
  const isCompactView = useIsCompactView();

  return (
    <div className="relative w-[100dvw] h-[100dvh] overflow-hidden flex flex-col z-50 bg-gradient-hero-dark">
      {/* Logo */}
      <div className="p-5">
        <LogoWhite className="h-[26px] w-[126px]" />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6">
        {isLoading ? (
          <LoadingContent />
        ) : (
          <ErrorContent
            info={info}
            actionBtn={actionBtn}
            isCompactView={isCompactView}
          />
        )}
      </div>

      {/* Footer */}
      <WelcomeFooter />
    </div>
  );
}

function LoadingContent() {
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <LuLoaderCircle className="animate-spin text-white" size={32} />
      <p
        className={`${typography.size["2xl"]} ${typography.weight.bold} mb-2`}
        style={{ color: colors.hex.white }}
      >
        Loading...
      </p>
    </div>
  );
}

function ErrorContent({
  info,
  actionBtn,
  isCompactView,
}: {
  info: string;
  actionBtn?: {
    label: string;
    onClick: () => void;
  };
  isCompactView: boolean;
}) {
  return (
    <div
      className={`w-full max-w-[1000px] flex items-center ${
        isCompactView ? "flex-col pb-5 px-4" : ""
      }`}
    >
      {/* Error message */}
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center text-white">
        <p
          className={`${
            isCompactView ? typography.size["3xl"] : "text-5xl"
          } ${typography.weight.bold} mb-2 drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)]`}
        >
          Sorry!
        </p>

        <p
          className={`${typography.size.xl} drop-shadow-[0_2px_2px_rgba(0,0,0,0.4)]`}
        >
          {info}
        </p>

        {actionBtn && (
          <button
            className="bg-primary-blue text-white px-4 py-2 mt-5 rounded-md
            hover:brightness-150 hover:cursor-pointer transition-all duration-300"
            onClick={actionBtn.onClick}
          >
            {actionBtn.label}
          </button>
        )}
      </div>
    </div>
  );
}
